import React from 'react';
import type { EpPIBriefResult } from '@types';

// ── Colour helpers ──────────────────────────────────────────────────────────
const threatColor = (level: string) => {
    const l = level?.toLowerCase();
    if (l === 'critical') return 'bg-red-600 text-white border-red-700';
    if (l === 'high') return 'bg-orange-500 text-white border-orange-600';
    if (l === 'medium') return 'bg-yellow-500 text-slate-900 border-yellow-600';
    return 'bg-emerald-500 text-white border-emerald-600';
};

// ── Card sub-component ──────────────────────────────────────────────────────
const Card: React.FC<{ icon: string; title: string; content: string; urgent?: boolean }> = ({ icon, title, content, urgent }) => (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${urgent ? 'border-orange-300' : 'border-slate-200'}`}>
        <div className={`px-5 py-3 border-b ${urgent ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-100'}`}>
            <h3 className={`text-sm font-bold ${urgent ? 'text-orange-800' : 'text-slate-700'} flex items-center gap-2`}>{icon} {title}</h3>
        </div>
        <p className="p-5 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{content}</p>
    </div>
);

// ── Main Display Component ──────────────────────────────────────────────────
interface EpPIBriefDisplayProps {
    /** The structured PI brief result from the backend */
    report: EpPIBriefResult;
    /**
     * Optional movement metadata captured at form submission.
     * Passed through so the classification banner can display operation,
     * principal, and movement details without re-fetching.
     */
    formSnapshot?: {
        operation_name?: string;
        principal_name?: string;
        movement_date?: string;
        movement_time?: string;
        movement_type?: string;
        origin_location?: string;
        destination?: string;
        detail_size?: string;
        vehicle_count?: string;
    };
}

const EpPIBriefDisplay: React.FC<EpPIBriefDisplayProps> = ({ report: parsed, formSnapshot }) => {
    const opName = (parsed as any).operationName || formSnapshot?.operation_name || 'Classified Operation';
    const principal = (parsed as any).principalName || formSnapshot?.principal_name || '—';
    const movDate = (parsed as any).movementDate || formSnapshot?.movement_date || '—';
    const movTime = formSnapshot?.movement_time || '';
    const movType = (parsed as any).movementType || formSnapshot?.movement_type || '—';
    const origin = (parsed as any).originLocation || formSnapshot?.origin_location || '—';
    const dest = (parsed as any).destination || formSnapshot?.destination || '—';
    const detailSize = formSnapshot?.detail_size || '—';
    const vehicles = formSnapshot?.vehicle_count || '—';

    return (
        <div className="space-y-4 print:p-0">
            {/* ── Classification / Header Banner ── */}
            <div className="rounded-xl overflow-hidden shadow-sm border border-slate-700">
                <div className="bg-slate-900 px-5 py-4 flex items-start justify-between">
                    <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Pre-Movement Intelligence Brief</p>
                        <h2 className="text-xl font-bold text-white">{opName}</h2>
                        <p className="text-sm text-slate-300 mt-0.5">
                            {principal} &nbsp;&middot;&nbsp; {movDate} {movTime} &nbsp;&middot;&nbsp; {movType}
                        </p>
                    </div>
                    {parsed.threatLevel && (
                        <span className={`text-sm font-black uppercase tracking-widest px-4 py-1.5 rounded-full border ${threatColor(parsed.threatLevel)}`}>
                            {parsed.threatLevel}
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-4 px-5 py-3 bg-slate-800 border-t border-slate-700 text-xs text-slate-300">
                    <span>&#128205; {origin} &rarr; {dest}</span>
                    <span>&#128739;&#65039; {movType}</span>
                    <span>&#128101; Detail: {detailSize}</span>
                    <span>&#128663; Vehicles: {vehicles}</span>
                </div>
            </div>

            {/* ── Structured display ── */}
            <div className="space-y-4">
                {parsed.overallAssessment && (
                    <Card icon="📋" title="Overall Threat Assessment" content={parsed.overallAssessment} />
                )}
                {parsed.aoIncidents && (
                    <Card icon="⚠️" title="AO Incidents (Last 24h)" content={parsed.aoIncidents} />
                )}
                {parsed.routeStatus && (
                    <Card icon="🛣️" title="Route Status" content={parsed.routeStatus} />
                )}
                {parsed.personOfConcernUpdate && (
                    <Card icon="👤" title="Person-of-Concern Update" content={parsed.personOfConcernUpdate} />
                )}
                {parsed.protectiveMeasures?.length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">🛡️ Recommended Protective Measures</h3>
                        </div>
                        <ul className="p-5 space-y-2">
                            {parsed.protectiveMeasures.map((m, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                                    {m}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {parsed.contingencyActions && (
                    <Card icon="🚨" title="Contingency Actions" content={parsed.contingencyActions} urgent />
                )}
                {parsed.weatherAndEnvironmental && (
                    <Card icon="🌤️" title="Weather & Environmental" content={parsed.weatherAndEnvironmental} />
                )}
                {parsed.commsAndMedical && (
                    <Card icon="📡" title="Comms & Medical Readiness" content={parsed.commsAndMedical} />
                )}
                {parsed.advanceAgentStatus && (
                    <Card icon="🔎" title="Advance Agent Status" content={parsed.advanceAgentStatus} />
                )}
            </div>

            {/* Footer */}
            <div className="text-center text-[10px] text-slate-400 py-4 border-t border-slate-200 print:block">
                PRE-MOVEMENT INTELLIGENCE BRIEF &nbsp;&middot;&nbsp; {opName} &nbsp;&middot;&nbsp; {movDate} &nbsp;&middot;&nbsp; IntelOptics Intelligence Platform
            </div>
        </div>
    );
};

export default EpPIBriefDisplay;
